import { apiClient } from './api';
import type {
  AddGapDraftCommentApiRequest,
  AddGapDraftCommentApiResponse,
  EditGapDraftApiRequest,
  EditGapDraftApiResponse,
  GetGapExecutionApiResponse,
  ListGapDraftEditsApiResponse,
  ListGapDraftsApiResponse,
  ListGapExecutionsApiResponse,
  ListKeptGapDraftsApiResponse,
  SetGapDraftReviewApiRequest,
  SetGapDraftReviewApiResponse,
} from '@momentum/types';

/**
 * The gap-review surface.
 *
 * The runs and a run's drafts are fetched separately, and the split is the
 * point: the runs are cheap and always needed, while a run's drafts are the
 * expensive half — each carries both of its gap layers whole. Loading the drafts
 * of every run to draw a picker is what made the queue take seconds to open.
 *
 * {@link listKeptDrafts} crosses runs anyway, because the drafts it returns are
 * only the ones a reviewer kept — see its own note.
 */
export const gapReviewService = {
  /**
   * Every run of the company, newest first, with its draft tally. Counted
   * server-side, so this answers "how many gaps did that run produce" without
   * fetching one.
   */
  async listExecutions(): Promise<ListGapExecutionsApiResponse> {
    return (await apiClient.get('/gap-review/executions')).data;
  },

  /** One run's drafts. The run is required — there is no corpus-wide listing. */
  async listDrafts(executionId: string): Promise<ListGapDraftsApiResponse> {
    return (
      await apiClient.get('/gap-review/drafts', { params: { executionId } })
    ).data;
  },

  /**
   * Every kept draft of the company, across every run — the final-review list.
   *
   * The one cross-run read here, and the exception the rule above is about: a
   * kept draft is one a reviewer promoted by hand, so this grows a decision at a
   * time rather than with every execution performed.
   */
  async listKeptDrafts(): Promise<ListKeptGapDraftsApiResponse> {
    return (await apiClient.get('/gap-review/drafts/kept')).data;
  },

  async setDraftReview(
    draftId: string,
    request: SetGapDraftReviewApiRequest
  ): Promise<SetGapDraftReviewApiResponse> {
    return (
      await apiClient.put(`/gap-review/drafts/${draftId}/review`, request)
    ).data;
  },

  /**
   * One reviewer's pass over the product layer. Every change of a save travels
   * in one request: the server applies them together or refuses them together,
   * so a half-saved pass can never be recorded as a whole one.
   */
  async editDraftGap(
    draftId: string,
    request: EditGapDraftApiRequest
  ): Promise<EditGapDraftApiResponse> {
    return (await apiClient.put(`/gap-review/drafts/${draftId}/gap`, request))
      .data;
  },

  /**
   * One draft's edit history. Its own request rather than a field on the draft:
   * the queue loads a whole run's drafts at once, and the history grows without
   * bound while nobody is looking at it.
   */
  async listDraftEdits(draftId: string): Promise<ListGapDraftEditsApiResponse> {
    return (await apiClient.get(`/gap-review/drafts/${draftId}/edits`)).data;
  },

  async addDraftComment(
    draftId: string,
    request: AddGapDraftCommentApiRequest
  ): Promise<AddGapDraftCommentApiResponse> {
    return (
      await apiClient.post(`/gap-review/drafts/${draftId}/comments`, request)
    ).data;
  },

  async getExecution(executionId: string): Promise<GetGapExecutionApiResponse> {
    return (await apiClient.get(`/gap-review/executions/${executionId}`)).data;
  },
};
